import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Plus, Trash01, Key01, Edit01, Server01 } from "@untitledui/icons";
import { Table } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppDialog } from "@/components/app/AppDialog";
import { ApiError } from "@/lib/apiClient";
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useResetPassword,
  useChangeAuthSource,
  useAuditLog,
  roleLabel,
  USER_ROLES,
  type UserSummary,
} from "@/lib/users";
import { useDirectoryProviders } from "@/lib/directory";
import { formatDateTime } from "@/lib/format";
import { AppSelect } from "@/components/app/AppSelect";
import { IconAction } from "@/components/app/IconAction";

const inputCls =
  "w-full rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";

function apiErrText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string; error?: string } | string | null;
    if (typeof body === "string" && body) return body;
    if (body && typeof body === "object") return body.message ?? body.error ?? e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

export function UsersPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useUsers();
  const [creating, setCreating] = useState(false);
  const [editUser, setEditUser] = useState<UserSummary | null>(null);
  const [resetUser, setResetUser] = useState<UserSummary | null>(null);
  const [deleteUser, setDeleteUser] = useState<UserSummary | null>(null);
  const [authSrcUser, setAuthSrcUser] = useState<UserSummary | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("users.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("users.subtitle")}</SectionHeader.Subheading>
          </div>
          <SectionHeader.Actions>
            <Button size="md" color="primary" iconLeading={Plus} onPress={() => setCreating(true)}>
              {t("users.add")}
            </Button>
          </SectionHeader.Actions>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && (
        <div className="overflow-hidden rounded-xl ring-1 ring-secondary">
          <Table aria-label={t("users.title")} selectionMode="none">
            <Table.Header>
              <Table.Head isRowHeader id="username" label={t("users.col.username")} />
              <Table.Head id="displayName" label={t("users.col.displayName")} />
              <Table.Head id="roles" label={t("users.col.roles")} />
              <Table.Head id="enabled" label={t("users.col.status")} />
              <Table.Head id="authSource" label={t("users.col.source")} />
              <Table.Head id="lockedUntil" label={t("users.col.lockedUntil")} />
              <Table.Head id="lastLoginAt" label={t("users.col.lastLogin")} />
              <Table.Head id="actions" label="" />
            </Table.Header>
            <Table.Body items={data}>
              {(u) => (
                <Table.Row id={u.id}>
                  <Table.Cell className="font-medium text-primary">{u.username}</Table.Cell>
                  <Table.Cell>{u.displayName ?? "—"}</Table.Cell>
                  <Table.Cell>
                    <span className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (
                        <Badge key={r} type="pill-color" size="sm" color={r === "ROLE_ADMIN" ? "brand" : "gray"}>
                          {roleLabel(r)}
                        </Badge>
                      ))}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge type="pill-color" size="sm" color={u.enabled ? "success" : "gray"}>
                      {u.enabled ? t("users.enabled") : t("users.disabled")}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="flex flex-col">
                      <span>{u.authSource}</span>
                      {u.externalIdentifier && <span className="font-mono text-xs text-tertiary">{u.externalIdentifier}</span>}
                      {u.bootstrap && <Badge type="pill-color" size="sm" color="warning">bootstrap</Badge>}
                    </span>
                  </Table.Cell>
                  <Table.Cell>{u.lockedUntil ? formatDateTime(u.lockedUntil) : "—"}</Table.Cell>
                  <Table.Cell>{formatDateTime(u.lastLoginAt)}</Table.Cell>
                  <Table.Cell>
                    <div className="flex justify-end gap-1">
                      <IconAction icon={Edit01} label={t("users.edit")} onPress={() => setEditUser(u)} />
                      <IconAction icon={Server01} label={t("users.changeAuthSource")} onPress={() => setAuthSrcUser(u)} />
                      <IconAction icon={Key01} label={t("users.resetPassword")} onPress={() => setResetUser(u)} />
                      {!u.bootstrap && (
                        <IconAction icon={Trash01} label={t("users.delete")} onPress={() => setDeleteUser(u)} />
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* Nedávný auth audit */}
      <RecentAuditPanel />

      {creating && <UserFormModal onClose={() => setCreating(false)} />}
      {editUser && <UserFormModal user={editUser} onClose={() => setEditUser(null)} />}
      {resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
      {deleteUser && <DeleteUserModal user={deleteUser} onClose={() => setDeleteUser(null)} />}
      {authSrcUser && <AuthSourceModal user={authSrcUser} onClose={() => setAuthSrcUser(null)} />}
    </div>
  );
}

/** Změna auth-source uživatele (LOCAL ↔ LDAP:<provider>). */
function AuthSourceModal({ user, onClose }: { user: UserSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const providers = useDirectoryProviders();
  const change = useChangeAuthSource(user.id);
  const [authSource, setAuthSource] = useState(user.authSource ?? "LOCAL");
  const [externalIdentifier, setExternalIdentifier] = useState(user.externalIdentifier ?? "");
  const [newPassword, setNewPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const isLdap = authSource.startsWith("LDAP:");

  const submit = async () => {
    setErr(null);
    if (isLdap && !externalIdentifier.trim()) return setErr(t("users.extIdRequired"));
    if (!isLdap && newPassword && newPassword.length < 12) return setErr(t("users.pwMin"));
    try {
      await change.mutateAsync({
        authSource,
        externalIdentifier: isLdap ? externalIdentifier : undefined,
        newPassword: !isLdap && newPassword ? newPassword : undefined,
      });
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("users.changeAuthSource")}
      description={user.username}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={change.isPending}>{t("common.cancel")}</Button>
          <Button color="primary" onPress={() => void submit()} isDisabled={change.isPending}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {err && <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
        <Field label={t("users.authSource")}>
          <AppSelect value={authSource} onChange={(e) => setAuthSource(e.target.value)}>
            <option value="LOCAL">LOCAL</option>
            {providers.data?.map((p) => (
              <option key={p.id} value={`LDAP:${p.id}`}>LDAP: {p.id}</option>
            ))}
          </AppSelect>
        </Field>
        {isLdap ? (
          <Field label={t("users.externalId")}>
            <input className={inputCls} value={externalIdentifier} placeholder="uid=jdoe,ou=people,dc=…" onChange={(e) => setExternalIdentifier(e.target.value)} />
          </Field>
        ) : (
          <Field label={t("users.newPasswordOptional")}>
            <input className={inputCls} type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </Field>
        )}
      </div>
    </AppDialog>
  );
}

/** Nedávné auth audit události (posledních 25) přímo na Users tabu. */
function RecentAuditPanel() {
  const { t } = useTranslation();
  const { data, isLoading } = useAuditLog(0, 25);
  return (
    <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-md font-semibold text-primary">{t("users.recentAudit")}</h2>
        {/* Panel je jen náhled posledních událostí — filtrování, stránkování a export
            má plná stránka Auditu, tak sem místo duplikace vede odkaz. */}
        <Link
          to="/settings/audit"
          className="text-sm font-medium text-brand-secondary hover:underline"
        >
          {t("users.recentAudit.showAll")}
        </Link>
      </div>
      {isLoading && <LoadingIndicator />}
      {data && data.content.length === 0 && <p className="text-sm text-tertiary">{t("audit.empty")}</p>}
      {data && data.content.length > 0 && (
        <div className="overflow-hidden rounded-xl ring-1 ring-secondary">
          <Table aria-label={t("users.recentAudit")} selectionMode="none" size="sm">
            <Table.Header>
              <Table.Head isRowHeader id="timestamp" label={t("audit.time")} />
              <Table.Head id="eventType" label={t("audit.event")} />
              <Table.Head id="outcome" label={t("audit.outcome")} />
              <Table.Head id="username" label={t("tokens.col.username")} />
              <Table.Head id="ipAddress" label={t("audit.ip")} />
            </Table.Header>
            <Table.Body items={data.content.slice(0, 25)}>
              {(e) => (
                <Table.Row
                  id={e.id}
                  className={e.outcome === "FAILURE" || e.outcome === "DENIED" ? "bg-error-primary/5" : undefined}
                >
                  <Table.Cell className="whitespace-nowrap text-tertiary">{formatDateTime(e.timestamp, { seconds: true })}</Table.Cell>
                  <Table.Cell className="font-medium text-primary">{e.eventType}</Table.Cell>
                  <Table.Cell>
                    <Badge
                      type="pill-color"
                      size="sm"
                      color={e.outcome === "SUCCESS" ? "success" : e.outcome === "FAILURE" || e.outcome === "DENIED" ? "error" : "gray"}
                    >
                      {e.outcome}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-secondary">{e.username ?? e.actor ?? "—"}</Table.Cell>
                  <Table.Cell className="text-tertiary">{e.ipAddress ?? "—"}</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
}

const ROLE_DESC_KEY: Record<string, string> = {
  ROLE_ADMIN: "users.role.admin.desc",
  ROLE_OPERATOR: "users.role.operator.desc",
  ROLE_VIEWER: "users.role.viewer.desc",
};

function RolesField({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-secondary">{t("users.col.roles")}</span>
      <div className="flex flex-col gap-2">
        {USER_ROLES.map((r) => (
          <label key={r} className="flex items-start gap-2 text-sm text-primary">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={value.includes(r)}
              onChange={(e) => onChange(e.target.checked ? [...value, r] : value.filter((x) => x !== r))}
            />
            <span className="flex flex-col">
              <span className="font-medium">{roleLabel(r)}</span>
              {ROLE_DESC_KEY[r] && <span className="text-xs text-tertiary">{t(ROLE_DESC_KEY[r])}</span>}
            </span>
          </label>
        ))}
      </div>
      <span className="text-xs text-tertiary">{t("users.roles.hint")}</span>
    </div>
  );
}

function UserFormModal({ user, onClose }: { user?: UserSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const isEdit = !!user;
  const create = useCreateUser();
  const update = useUpdateUser(user?.id ?? "");
  const [serverError, setServerError] = useState<string | null>(null);

  const providers = useDirectoryProviders();
  const schema = z.object({
    username: isEdit ? z.string().optional() : z.string().min(1),
    displayName: z.string().optional(),
    email: z.string().email().optional().or(z.literal("")),
    password: z.string().optional(),
    roles: z.array(z.string()).min(1, t("users.rolesMin")),
    enabled: z.boolean().optional(),
    authSource: z.string().optional(),
    externalIdentifier: z.string().optional(),
  });
  type FormVals = z.infer<typeof schema>;

  const { register, handleSubmit, watch, setValue, formState } = useForm<FormVals>({
    resolver: zodResolver(schema),
    defaultValues: {
      username: user?.username ?? "",
      displayName: user?.displayName ?? "",
      email: user?.email ?? "",
      password: "",
      roles: user?.roles ?? ["ROLE_VIEWER"],
      enabled: user?.enabled ?? true,
      authSource: "LOCAL",
      externalIdentifier: "",
    },
  });
  const roles = watch("roles");
  const enabled = watch("enabled");
  const authSource = watch("authSource") ?? "LOCAL";
  const isLdap = authSource.startsWith("LDAP:");

  const onSubmit = handleSubmit(async (vals) => {
    setServerError(null);
    const ldap = (vals.authSource ?? "LOCAL").startsWith("LDAP:");
    if (!isEdit && !ldap && (vals.password ?? "").length < 12) {
      setServerError(t("users.pwMin"));
      return;
    }
    if (!isEdit && ldap && !(vals.externalIdentifier ?? "").trim()) {
      setServerError(t("users.extIdRequired"));
      return;
    }
    try {
      if (isEdit) {
        await update.mutateAsync({ displayName: vals.displayName, email: vals.email || undefined, roles: vals.roles, enabled: !!vals.enabled });
      } else {
        await create.mutateAsync({
          username: vals.username!,
          displayName: vals.displayName,
          email: vals.email || undefined,
          password: ldap ? undefined : vals.password!,
          roles: vals.roles,
          authSource: vals.authSource,
          externalIdentifier: ldap ? vals.externalIdentifier : undefined,
        });
      }
      onClose();
    } catch (e) {
      setServerError(apiErrText(e));
    }
  });

  const busy = create.isPending || update.isPending;

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={isEdit ? t("users.editTitle") : t("users.add")}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={busy}>{t("common.cancel")}</Button>
          <Button color="primary" onPress={() => void onSubmit()} isDisabled={busy}>{t("common.save")}</Button>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)}>
        {serverError && <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{serverError}</div>}
        {!isEdit && (
          <Field label={t("users.col.username")} error={formState.errors.username?.message}>
            <input className={inputCls} autoFocus {...register("username")} />
          </Field>
        )}
        <Field label={t("users.col.displayName")}>
          <input className={inputCls} {...register("displayName")} />
        </Field>
        <Field label="Email" error={formState.errors.email?.message}>
          <input className={inputCls} type="email" {...register("email")} />
        </Field>
        {!isEdit && (
          <Field label={t("users.authSource")}>
            <AppSelect {...register("authSource")}>
              <option value="LOCAL">LOCAL</option>
              {providers.data?.map((p) => (
                <option key={p.id} value={`LDAP:${p.id}`}>LDAP: {p.id}</option>
              ))}
            </AppSelect>
          </Field>
        )}
        {!isEdit && isLdap && (
          <Field label={t("users.externalId")}>
            <input className={inputCls} placeholder="uid=jdoe,ou=people,dc=…" {...register("externalIdentifier")} />
          </Field>
        )}
        {!isEdit && !isLdap && (
          <Field label={t("login.password")} error={formState.errors.password?.message}>
            <input className={inputCls} type="password" autoComplete="new-password" {...register("password")} />
          </Field>
        )}
        <div>
          <RolesField value={roles} onChange={(v) => setValue("roles", v, { shouldValidate: true })} />
          {formState.errors.roles && <p className="mt-1 text-xs text-error-primary">{formState.errors.roles.message as string}</p>}
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-primary">
            <input type="checkbox" checked={!!enabled} onChange={(e) => setValue("enabled", e.target.checked)} />
            {t("users.enabledLabel")}
          </label>
        )}
      </form>
    </AppDialog>
  );
}

function ResetPasswordModal({ user, onClose }: { user: UserSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const reset = useResetPassword(user.id);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (pw.length < 12) return setErr(t("users.pwMin"));
    try {
      await reset.mutateAsync(pw);
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("users.resetPassword")}
      description={user.username}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={reset.isPending}>{t("common.cancel")}</Button>
          <Button color="primary" onPress={() => void submit()} isDisabled={reset.isPending}>{t("common.save")}</Button>
        </>
      }
    >
      {err && <div className="mb-3 rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
      <Field label={t("users.newPassword")}>
        <input className={inputCls} type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
      </Field>
    </AppDialog>
  );
}

function DeleteUserModal({ user, onClose }: { user: UserSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const del = useDeleteUser();
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    try {
      await del.mutateAsync(user.id);
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("users.deleteTitle")}
      description={t("users.deleteConfirm", { username: user.username })}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={del.isPending}>{t("common.cancel")}</Button>
          <Button color="primary-destructive" onPress={() => void submit()} isDisabled={del.isPending}>{t("users.delete")}</Button>
        </>
      }
    >
      {err && <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
    </AppDialog>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-secondary">{label}</span>
      {children}
      {error && <span className="text-xs text-error-primary">{error}</span>}
    </label>
  );
}
